/******/ (() => { // webpackBootstrap
class ProductCardBasic extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('#ProductCardBasicAtcForm');
    this.quickViewLink = this.querySelector('[data-quick-view-link]');
    this.cartCountIndicator = document.querySelector('[data-cart-count-indicator]');
    this.attachEvents = this.attachEvents.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.updateCartCountIndicator = this.updateCartCountIndicator.bind(this);
    this.handleQuickView = this.handleQuickView.bind(this);
    this.wethemeGlobal = document.querySelector('script#wetheme-global');
    this.translationsObject = JSON.parse(this.wethemeGlobal.textContent);
  }

  connectedCallback() {
    window.wetheme.webcomponentRegistry.register({key: 'component-product-card-basic'});
    this.attachEvents();
  }

  attachEvents() {
    if (this.form) {
      this.form.addEventListener('submit', this.handleSubmit);
    }

    if (this.quickViewLink) {
      this.quickViewLink.addEventListener('click', this.handleQuickView);
    }
  }

  async handleSubmit(e) {
    e.preventDefault();

    const formData = new FormData(this.form);
    const requestKey = this.getQuickAddRequestKey(formData);
    const requestState = this.getQuickAddRequestState();

    // FN-AS-17571: Prevent overlapping recommendation quick-add requests for the same variant.
    if (this.form.dataset.quickAddPending === 'true' || requestState.inFlightKeys.has(requestKey)) {
      return false;
    }

    requestState.inFlightKeys.add(requestKey);
    const cartRequestId = this.getNextQuickAddCartRequestId(requestState);
    this.form.dataset.quickAddPending = 'true';

    /* ===== Set the loading state (spinner) - fix button size to prevent content shift ===== */
    const addButton = e.currentTarget.querySelector('button');
    const originalButtonHtml = addButton?.innerHTML;
    if (addButton) {
      addButton.style.width = `${addButton.getBoundingClientRect().width}px`;
      addButton.style.height = `${addButton.getBoundingClientRect().height}px`;
      addButton.disabled = true;
      addButton.setAttribute('aria-disabled', 'true');
      addButton.innerHTML = `<svg viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="1.2em" height="1.2em" class="spin flex-full"><g clip-path="url(#clip0_3605_47041)"><path d="M12.5 23C6.42487 23 1.5 18.0751 1.5 12C1.5 5.92487 6.42487 1 12.5 1C18.5751 1 23.5 5.92487 23.5 12C23.5 15.1767 22.1534 18.0388 20 20.0468" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></g><defs><clipPath id="clip0_3605_47041"><rect width="24" height="24" fill="none" transform="translate(0.5)"/></clipPath></defs>${ this.translationsObject.translations.loading }</svg>`;
      addButton.classList.add('is-loading');
    }

    let addedToCart = false;
    
    try {
      /* ===== Add item to the cart ===== */
      // FN-AS-17571: Normalize recommendation quick-add into one cart line per plain variant.
      const responseJson = await this.addOrIncrementQuickAdd(formData);
      if (!responseJson) return false;

      /* ===== Re-render cart drawer ===== */
      responseJson._cartRequestId = cartRequestId;
      await this.updateCartDrawer(responseJson);
      addedToCart = true;

      /* ===== Update cart count ===== */
      this.updateCartCountIndicator(responseJson);
    } catch (e) {
      console.error('Unable to add to cart: ', e);
    } finally {
      requestState.inFlightKeys.delete(requestKey);
      this.form.dataset.quickAddPending = 'false';

      if (addButton && addButton.isConnected) {
        addButton.disabled = false;
        addButton.setAttribute('aria-disabled', 'false');

        if (!addedToCart && originalButtonHtml) {
          addButton.innerHTML = originalButtonHtml;
          addButton.classList.remove('is-loading');
          addButton.style.width = '';
          addButton.style.height = '';
        }
      }
    }

    return false;
  }

  getQuickAddRequestState() {
    // FN-AS-17571: Reuse the same quick-add request state as collection/homepage product cards.
    if (!window.wetheme.quickAddRequestState) {
      window.wetheme.quickAddRequestState = {
        inFlightKeys: new Set(),
        latestCartRequestId: 0
      };
    }

    return window.wetheme.quickAddRequestState;
  }

  getQuickAddRequestKey(formData) {
    return `variant:${formData.get('id') || ''}`;
  }

  getNextQuickAddCartRequestId(requestState) {
    // FN-AS-17571: Keep quick-add request ids ahead of any drawer render ids already issued.
    const cartDrawer = document.querySelector('cart-drawer');
    const latestDrawerRequestId = Number(cartDrawer?.getLatestRequestedCartUpdateId?.() || 0);
    const cartRequestId = Math.max(requestState.latestCartRequestId, latestDrawerRequestId) + 1;
    requestState.latestCartRequestId = cartRequestId;
    return cartRequestId;
  }

  async updateCartDrawer(responseJson) {
    // FN-AS-17571: Await the drawer render before releasing this quick-add guard.
    const cartDrawer = document.querySelector('cart-drawer');

    if (cartDrawer?.handleCartUpdate) {
      await cartDrawer.handleCartUpdate(responseJson);
      return;
    }

    window.eventBus.emit('update:cart:drawer', responseJson);
  }

  getQuickAddVariantId(formData) {
    // FN-AS-17571: Use the variant id as the merge key for quick-add cart normalization.
    return Number(formData.get('id'));
  }

  getQuickAddQuantity(formData) {
    const quantity = Number.parseInt(formData.get('quantity') || '1', 10);
    return Number.isNaN(quantity) || quantity < 1 ? 1 : quantity;
  }

  async getAjaxCart() {
    // FN-AS-17571: Read the live cart before quick-add so existing same-variant lines can be incremented.
    const response = await fetch('/cart.js', {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json'
      }
    });

    if (!response.ok) return null;

    return response.json();
  }

  hasVisibleLineItemProperties(item) {
    if (!item?.properties) return false;

    const entries = Array.isArray(item.properties) ? item.properties : Object.entries(item.properties);
    return entries.some(([key, value]) => value && !String(key).startsWith('_'));
  }

  hasVisibleFormLineItemProperties(formData) {
    return Array.from(formData.entries()).some(([key, value]) => {
      const propertyMatch = String(key).match(/^properties\[(.+)\]$/);
      return propertyMatch && value && !propertyMatch[1].startsWith('_');
    });
  }

  isMergeableCartItem(item, variantId) {
    return Number(item?.variant_id) === variantId && !item.selling_plan_allocation && !item.has_components && !item.discounts?.length && !item.line_level_discount_allocations?.length && !this.hasVisibleLineItemProperties(item);
  }

  hasRenderedCartLineForVariant(variantId) {
    return Boolean(document.querySelector(`cart-quantity[data-item-key^="${variantId}:"]`));
  }

  shouldIncrementExistingCartLine(formData, variantId) {
    // FN-AS-17571: Only pay for the cart lookup when the drawer already contains this plain variant.
    return Boolean(variantId) && !formData.get('selling_plan') && !this.hasVisibleFormLineItemProperties(formData) && this.hasRenderedCartLineForVariant(variantId);
  }

  async changeCartLine(id, quantity) {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        id,
        quantity,
        sections: 'cart-drawer'
      })
    });

    if (!response.ok) return null;

    return response.json();
  }

  async addCartLine(formData) {
    const response = await fetch(window.routes.cart_add_url, this.fetchConfigWithBody(formData));

    if (!response || !response.ok) return null;

    return response.json();
  }

  async addOrIncrementQuickAdd(formData) {
    // FN-AS-17571: Increment an existing same-variant cart line instead of letting Shopify create hidden-property duplicates.
    const variantId = this.getQuickAddVariantId(formData);
    const quantityToAdd = this.getQuickAddQuantity(formData);
    if (!this.shouldIncrementExistingCartLine(formData, variantId)) {
      return this.addCartLine(formData);
    }

    const cart = variantId ? await this.getAjaxCart() : null;
    const matchingItems = cart?.items?.filter(item => this.isMergeableCartItem(item, variantId)) || [];

    if (!matchingItems.length) {
      return this.addCartLine(formData);
    }

    const targetItem = matchingItems[0];
    const nextQuantity = matchingItems.reduce((sum, item) => sum + item.quantity, 0) + quantityToAdd;
    let responseJson = await this.changeCartLine(targetItem.key, nextQuantity);
    if (!responseJson) return null;

    for (const duplicateItem of matchingItems.slice(1)) {
      responseJson = await this.changeCartLine(duplicateItem.key, 0);
      if (!responseJson) return null;
    }

    return responseJson;
  }

  fetchConfigWithBody(formData = new FormData(this.form)) {
    /* ===== Create the fetch configuration object with the form data as the body ===== */
    const config = this.fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = formData;
    return config;
  }

  fetchConfig(type = 'json') {
    /* ===== Create the fetch configuration object ===== */
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
    };
  }

  async getCart() {
    /* ===== Fetch the cart information ===== */
    try {
      const response = await fetch(`${window.routes.cart_url}?view=compare`);
      return await response.json();
    } catch (error) {
      console.error(`Error fetching cart: ${error.description || error.message}`);
    }
  }

  updateCartCountIndicator(responseJson) {
    /* ===== Update the cart count indicator ===== */
    if (!this.cartCountIndicator) return;
    // FN-AS-17571: Use the full cart returned by /cart/change.js so theme-editor counters do not wait on a stale extra fetch.
    if (typeof responseJson?.item_count === 'number') {
      window.wetheme.updateCartCount(responseJson);
      return;
    }

    this.getCart().then(cart => window.wetheme.updateCartCount(cart));
  }

  handleQuickView(e) {
    e.preventDefault();
    /* ===== Close cart drawer ===== */
    window.eventBus.emit('close:all:drawers');
    /* ===== Navigate to product page ===== */
    window.location.href = e.currentTarget.href;
  }

  detachEvents() {
    if (this.form) {
      this.form.removeEventListener('submit', this.handleSubmit);
    }

    if (this.quickViewLink) {
      this.quickViewLink.removeEventListener('click', this.handleQuickView);
    }
  }

  disconnectedCallback() {
    this.detachEvents();
  }
}

customElements.define('product-card-basic', ProductCardBasic);
/******/ })()
;
